'use client'

import Link from 'next/link'

import { cn } from '@/lib/utils'

// Paying clients only (HubSpot: customer lifecycle with a Closed Won deal), rebuilt 2026-09-30.
// Held back: St. Mark's School of Texas (order form requires written logo consent), and St. James School
// and Bethany School until we confirm they are still paying. Never add trial or prospect schools here.
// Never add without written approval: Second Baptist School (order form SC5) and Hockaday School (SC9)
// delete MSA 17.1 and 17.2. Checked all signed order forms 2026-10-08; North Shore and Marin Catholic added.
// `shape` sets the height so wordmarks, lockups, and crests read at a similar visual weight.
// `story` links the logo to that school's story on our own site (a webinar page, a case study).
// Only link to pages on schooljourney.com: never to a school's own website.
// `onDark` marks files that are white-on-dark versions: in full color they sit on a dark tile, or the
// lettering vanishes on white. Swap in a light-background file when the school sends one.
type Shape = 'wordmark' | 'lockup' | 'crest'

const logos: { name: string; file: string; shape: Shape; story?: string; onDark?: boolean }[] = [
  { name: 'Miami Country Day School', file: 'miami-country-day-school', shape: 'crest' },
  {
    name: 'Westminster Christian School',
    file: 'westminster-christian-school',
    shape: 'lockup',
    story: '/webinars/westminster'
  },
  {
    name: 'Thomas Jefferson Independent Day School',
    file: 'thomas-jefferson-independent-day-school',
    shape: 'wordmark'
  },
  { name: 'Montgomery Bell Academy', file: 'montgomery-bell-academy', shape: 'crest' },
  { name: 'GISSV, German International School of Silicon Valley', file: 'gissv', shape: 'wordmark' },
  { name: "King's Ridge Christian School", file: 'kings-ridge-christian-school', shape: 'lockup' },
  { name: 'Riverstone International School', file: 'riverstone-international-school', shape: 'wordmark', onDark: true },
  { name: 'Catholic Memorial School', file: 'catholic-memorial-school', shape: 'crest' },
  { name: 'Virginia Academy', file: 'virginia-academy', shape: 'wordmark' },
  { name: 'Sacred Heart Academy', file: 'sacred-heart-academy', shape: 'wordmark' },
  { name: 'Westminster School for Young Children', file: 'westminster-school-for-young-children', shape: 'lockup' },
  { name: 'Life Christian Academy', file: 'life-christian-academy', shape: 'wordmark' },
  { name: 'Coastal Christian Preparatory School', file: 'coastal-christian-preparatory-school', shape: 'crest' },
  { name: "St. Anne's School of Annapolis", file: 'st-annes-school-of-annapolis', shape: 'wordmark', onDark: true },
  { name: 'Salem Lutheran School', file: 'salem-lutheran-school', shape: 'lockup' },
  { name: 'New Horizon School', file: 'new-horizon-school', shape: 'wordmark' },
  { name: 'Reich Hebrew Academy', file: 'reich-hebrew-academy', shape: 'wordmark', onDark: true },
  { name: 'North Shore Country Day School', file: 'north-shore-country-day-school', shape: 'wordmark', onDark: true },
  { name: 'Marin Catholic High School', file: 'marin-catholic-high-school', shape: 'wordmark', onDark: true }
]

const shapeClass: Record<Shape, string> = {
  wordmark: 'h-8 max-w-[220px]',
  lockup: 'h-12 max-w-[180px]',
  crest: 'h-14 max-w-[90px]'
}

/**
 * At rest the logo is a gray silhouette. On hover or keyboard focus it shows in the school's own colors
 * (on a white tile in dark mode, so dark logos stay readable) with the school's name underneath.
 */
const LogoItem = ({ name, file, shape, story, onDark, hidden }: (typeof logos)[number] & { hidden?: boolean }) => {
  const logo = (
    <>
      <span
        className={cn(
          'flex items-center rounded-lg px-3 py-2 transition-colors duration-300',
          onDark
            ? 'group-hover/logo:bg-[#1E293B] group-focus-visible/logo:bg-[#1E293B]'
            : 'dark:group-hover/logo:bg-white dark:group-focus-visible/logo:bg-white'
        )}
      >
        <img
          src={`/img/logo/schools-marquee/clients/${file}.png`}
          alt={hidden ? '' : name}
          // Eager on purpose: the two tracks each scroll by their own width, so a logo that loads late in one
          // copy makes that track narrower and slower and the copies drift into each other.
          loading="eager"
          decoding="async"
          className={cn(
            'w-auto object-contain transition-[filter,opacity] duration-300',
            shapeClass[shape],
            // Silhouettes: dark gray in light mode, white in dark mode; full color on hover
            'brightness-0 opacity-60 group-hover/logo:brightness-100 group-hover/logo:opacity-100 group-focus-visible/logo:brightness-100 group-focus-visible/logo:opacity-100',
            'dark:invert dark:opacity-75 dark:group-hover/logo:invert-0 dark:group-hover/logo:opacity-100 dark:group-focus-visible/logo:invert-0 dark:group-focus-visible/logo:opacity-100'
          )}
        />
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-full left-1/2 mt-2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap text-[#0F517B] opacity-0 transition-opacity duration-300 group-hover/logo:opacity-100 group-focus-visible/logo:opacity-100 dark:text-[#BAD7EC]"
      >
        {name}
        {story && <span className="text-[#1D6693] dark:text-[#FFC933]"> · Their story →</span>}
      </span>
    </>
  )
  return (
    <li className="relative flex shrink-0 items-center justify-center" aria-hidden={hidden || undefined}>
      {story ? (
        <Link
          href={story}
          tabIndex={hidden ? -1 : undefined}
          aria-label={hidden ? undefined : `${name}: read their story`}
          className="group/logo relative flex items-center rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0F517B] dark:focus-visible:outline-[#BAD7EC]"
        >
          {logo}
        </Link>
      ) : (
        <span className="group/logo relative flex cursor-default items-center">{logo}</span>
      )}
    </li>
  )
}

/** Scrolling row of paying clients. Pauses on hover; holds still for reduced-motion users. */
export function MarqueeDemo() {
  const track =
    'flex shrink-0 items-center gap-(--gap) animate-marquee group-hover:[animation-play-state:paused] group-focus-within:animate-none motion-reduce:animate-none motion-reduce:w-full motion-reduce:shrink motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-6'
  return (
    <div
      className="group relative flex overflow-hidden py-8 [--duration:70s] [--gap:3.5rem] [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)] motion-reduce:[mask-image:none]"
      style={{ gap: 'var(--gap)' }}
    >
      <ul className={track}>
        {logos.map(logo => (
          <LogoItem key={logo.file} {...logo} />
        ))}
      </ul>
      <ul className={cn(track, 'motion-reduce:hidden')} aria-hidden>
        {logos.map(logo => (
          <LogoItem key={logo.file} {...logo} hidden />
        ))}
      </ul>
    </div>
  )
}
